import React, { useEffect, useState } from 'react';
import { BadgeCheck, Clock3, Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import './ContactVerifyRow.css';

// One contact line (mobile or email) of a member / service provider in the admin panel: the value, a "Verified" or "Not verified"
// badge, and, while it is not verified, a way to send a 6-digit code to it (SMS for a mobile number, mail for an email) and to type in
// the code the person reads out. type: 'mobile' | 'email'. onChanged() reloads the page data after a change.
// variant: 'field' (the provider page's label / value grid) or 'detail' (the member profile page's right-aligned rows); icon is optional.
const ContactVerifyRow = ({ label, type, userId, value, verified, icon: Icon, variant = 'field', onChanged }) => {
    const [sent, setSent] = useState(false);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState('');
    const [wait, setWait] = useState(0);

    useEffect(() => {
        if (wait <= 0) return undefined;
        const timer = setTimeout(() => setWait(seconds => seconds - 1), 1000);
        return () => clearTimeout(timer);
    }, [wait]);

    // another account or another value: start clean
    useEffect(() => { setSent(false); setCode(''); setWait(0); }, [userId, value]);

    const send = async () => {
        setBusy('send');
        try {
            const res = await API.post(`/admin/users/${userId}/contact/send-code`, { type });
            toast.success(res.data.message || 'Code sent');
            if (res.data.alreadyVerified) { onChanged?.(); return; }
            setSent(true);
            setWait(30);
        } catch (error) {
            if (error.response?.data?.retryAfter) setWait(Math.ceil(error.response.data.retryAfter));
            toast.error(error.response?.data?.message || 'Could not send the code');
        } finally { setBusy(''); }
    };

    const verify = async (event) => {
        event?.preventDefault();
        if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
        setBusy('verify');
        try {
            const res = await API.post(`/admin/users/${userId}/contact/verify-code`, { type, code });
            toast.success(res.data.message || 'Verified');
            setSent(false); setCode('');
            onChanged?.();
        } catch (error) { toast.error(error.response?.data?.message || 'Could not verify the code'); } finally { setBusy(''); }
    };

    return (
        <div className={variant === 'detail' ? 'profile-detail-row cv-detail' : 'pd-field'}>
            <span>{label}</span>
            <div className="cv-row">
                <div className="cv-line">
                    {value ? <>{Icon && <Icon size={14} />}<strong>{value}</strong></> : <em className="cv-empty">Not added</em>}
                    {value && (verified
                        ? <span className="cv-badge is-ok"><BadgeCheck size={13} /> Verified</span>
                        : <span className="cv-badge is-wait"><Clock3 size={13} /> Not verified</span>)}
                    {value && !verified && !sent && (
                        <button type="button" className="cv-send" onClick={send} disabled={busy === 'send' || wait > 0}>
                            {busy === 'send' ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
                            {wait > 0 ? `Wait ${wait}s` : type === 'email' ? 'Send code to email' : 'Send code by SMS'}
                        </button>
                    )}
                </div>
                {value && !verified && sent && (
                    <form className="cv-code" onSubmit={verify}>
                        <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" maxLength={6}
                            placeholder="6-digit code" aria-label={`${label} verification code`} autoFocus />
                        <button type="submit" className="cv-primary" disabled={busy === 'verify' || code.length !== 6}>{busy === 'verify' ? 'Checking…' : 'Verify'}</button>
                        <button type="button" onClick={send} disabled={busy === 'send' || wait > 0}>{wait > 0 ? `Resend in ${wait}s` : 'Resend'}</button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ContactVerifyRow;
